<template>
  <div dusk="app-layout">
    <application-head :description="description" :title="title" />

    <div :dusk="duskAttribute" class="relative h-app">
      <div
        id="app-navigation-bar"
        :class="{
          fixed: hideNavigationBarOnScroll,
          sticky: !hideNavigationBarOnScroll,
          'translate-y-none': hideNavigationBarOnScroll && !isLastScrollDown,
          '-translate-y-full ease-in-out': hideNavigationBarOnScroll && isLastScrollDown,
        }"
        class="bg-white w-full z-50 top-0 duration-300 ease-in-out"
      >
        <navigation-bar />
      </div>

      <!-- Application Content -->
      <div
        :class="{
          'pt-16 lg:pt-[88px]': hideNavigationBarOnScroll,
        }"
        class="relative z-0 h-app"
      >
        <div
          class="mx-auto flex h-full w-full"
          :class="{ 'max-w-[1440px]': !fullWidth }"
        >
          <div class="relative h-app">
            <!--Sidebar-->
            <application-sidebar
              v-if="sidebar"
              :collapsed="isCollapsed"
              :current-route="route().current()"
              @collapse="handleToggle"
            />
            <transition
              enter-active-class="duration-100 ease-out"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition duration-100 ease-out"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <!-- Secondary Sidebar -->
              <div
                v-if="slots.secondarySideNavigation"
                id="secondary-side-navigation"
                class="fixed z-10 hidden h-full min-h-screen select-none border-r border-gray-50 border-opacity-50 bg-white py-12 w-[244px] min-w-[244px] xl:block"
                :class="{
                  'ml-16': isCollapsed,
                  'ml-[324px]': !isCollapsed,
                }"
              >
                <slot name="secondarySideNavigation" />
              </div>
            </transition>
          </div>
          <div
            ref="contentRef"
            class="relative h-full w-full"
            dusk="container"
            v-bind="mergedContainerProps"
          >
            <Transition appear mode="out-in" name="fade">
              <Suspense>
                <template #default>
                  <div class="contents" dusk="contents">
                    <slot />
                  </div>
                </template>
                <template #fallback>
                  <div class="inset-0 flex w-full items-center justify-center h-app" dusk="loading-indicator">
                    <svg
                      class="h-12 w-12 motion-safe:animate-spin text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </div>
                </template>
              </Suspense>
            </Transition>
          </div>
        </div>
      </div>
      <!-- /Application Content -->
    </div>

    <div v-if="$page.props.flash && enableFlashMessages">
      <flash-messages />
    </div>
  </div>
</template>

<script setup>
import useChatInbox from '@/Sparrow/Hooks/useChatInbox';
import useClarity from '@/Sparrow/Hooks/useClarity';
import useUser from '@/Sparrow/Hooks/useUser';
import NavigationBar from '@/Sparrow/Landing/NavigationBar/NavigationBar.vue';
import { isDefined, useScroll } from '@vueuse/core';
import {
  computed,
  defineAsyncComponent,
  onErrorCaptured,
  ref,
  useSlots,
  watch,
} from 'vue';
import ApplicationHead from '@/Sparrow/ApplicationHead.vue';
import useFacebookPixel from '@/Sparrow/Hooks/useFacebookPixel';
import { handleToggle, isCollapsed } from '@/Layouts/Sidebar/Sidebar';
import { toSlugCase } from '@/Utilities/String';

const ApplicationSidebar = defineAsyncComponent(() => import('@/Sparrow/ApplicationSidebar.vue'));
const FlashMessages = defineAsyncComponent(() => import('@/Sparrow/Components/FlashMessages.vue'));

const props = defineProps({
  description: {
    default: '',
    required: false,
    type: String,
  },
  title: {
    default: '',
    required: false,
    type: String,
  },
  sidebar: {
    default: true,
    required: false,
    type: Boolean,
  },
  fullWidth: {
    default: false,
    required: false,
    type: Boolean,
  },
  containerProps: {
    default: () => ({}),
    required: false,
    type: Object,
  },
  enableFlashMessages: {
    default: true,
    required: false,
    type: Boolean,
  },
  hideNavigationBarOnScroll: {
    default: false,
    required: false,
    type: Boolean,
  },
});

const contentRef = ref(null);
const isLastScrollDown = ref(false);

const slots = useSlots();

const { directions } = useScroll(window);

const mergedContainerProps = computed(() => ({
  class: [{
    'ml-0 lg:pl-[250px]': props.sidebar && !isCollapsed.value,
    'ml-0 pl-0 lg:pl-16': props.sidebar && isCollapsed.value,
  }, props.containerProps.class || ''],
}));
const duskAttribute = computed(() => (isDefined(props.title) ? toSlugCase(props.title) : null));

const { id: userId } = useUser();

const { subscribeToUserInbox } = useChatInbox(userId);

useFacebookPixel();
useClarity();

onErrorCaptured((error, instance, info) => {
  console.error(error, instance, info);
});

watch(directions, ({ bottom, top }) => {
  const isVerticalScrolling = bottom || top;
  if (isVerticalScrolling) {
    isLastScrollDown.value = bottom;
  }
});

if (window.Intercom !== undefined && typeof window.Intercom === 'function') {
  window?.Intercom('update', {
    hide_default_launcher: true,
  });
}

subscribeToUserInbox();
</script>
